import { Column, Index } from 'typeorm';
import type { AssociationMembershipStatus, AssociationRenewalDecision, AssociationRenewalStep } from '../../constants/associations';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17639, PD-17640: the tenant's standing with one directory association. A row
 * also means the association is on the tenant's list, whatever its status. The
 * association itself lives in the Workd-wide directory, so it is held by id.
 */
@Entity('association_membership')
@Index('idx_association_membership_directory', ['directoryAssociationId'])
export class AssociationMembershipEntity extends BaseEntity {
	@Column('int', { name: 'directoryAssociationId' })
	public directoryAssociationId!: number;

	/**
	 * The association's name when it was added. Task queries read it for a task's
	 * link, and they can't join across to the Workd-wide directory: a tenant without
	 * the grant would lose every task query, not just this one.
	 */
	@Column('varchar', { name: 'associationName', length: 255 })
	public associationName!: string;

	@Column('varchar', { name: 'status', length: 20, default: 'not_member' })
	public status!: AssociationMembershipStatus;

	@Column('date', { name: 'sinceDate', nullable: true })
	public sinceDate?: string | null;

	@Column('varchar', { name: 'tier', length: 120, nullable: true })
	public tier?: string | null;

	@Column('varchar', { name: 'memberNumber', length: 120, nullable: true })
	public memberNumber?: string | null;

	@Column('date', { name: 'renewalDate', nullable: true })
	public renewalDate?: string | null;

	@Column('decimal', { name: 'renewalFee', precision: 12, scale: 2, nullable: true })
	public renewalFee?: string | null;

	@Column('boolean', { name: 'autoRenew', default: 0 })
	public autoRenew!: boolean;

	/** Owns the renewal: its tasks go to this person. */
	@Column('int', { name: 'fk_user__id_owner', unsigned: true, nullable: true })
	public renewalOwnerUserId?: number | null;

	@Column('varchar', { name: 'mainContact', length: 255, nullable: true })
	public mainContact?: string | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes?: string | null;

	/** This renewal's decision. Cleared when the next term starts. */
	@Column('varchar', { name: 'renewalDecision', length: 20, nullable: true })
	public renewalDecision?: AssociationRenewalDecision | null;

	@Column('date', { name: 'renewalDecidedOn', nullable: true })
	public renewalDecidedOn?: string | null;
}

/** One paid term of a membership. The term history and its value read these. */
@Entity('association_membership_term')
@Index('idx_association_term_membership', ['membershipId'])
export class AssociationMembershipTermEntity extends BaseEntity {
	@Column('int', { name: 'fk_association_membership__id' })
	public membershipId!: number;

	@Column('date', { name: 'startsOn' })
	public startsOn!: string;

	@Column('date', { name: 'endsOn' })
	public endsOn!: string;

	@Column('decimal', { name: 'amount', precision: 12, scale: 2, nullable: true })
	public amount?: string | null;

	@Column('date', { name: 'paidOn', nullable: true })
	public paidOn?: string | null;

	@Column('varchar', { name: 'method', length: 20, nullable: true })
	public method?: string | null;

	/** The receipt, kept in the tenant's files. */
	@Column('int', { name: 'fk_file__id', unsigned: true, nullable: true })
	public receiptFileId?: number | null;
}

/** A renewal step's task, so a decision can close it and a new term can set the next. */
@Entity('association_renewal_task')
@Index('idx_association_renewal_task_membership', ['membershipId'])
export class AssociationRenewalTaskEntity extends BaseEntity {
	@Column('int', { name: 'fk_association_membership__id' })
	public membershipId!: number;

	@Column('varchar', { name: 'step', length: 20 })
	public step!: AssociationRenewalStep;

	/** The renewal this task belongs to. */
	@Column('date', { name: 'renewalDate' })
	public renewalDate!: string;

	@Column('int', { name: 'fk__event__id', unsigned: true })
	public eventId!: number;
}
